import { FINANCE_DOCUMENT_TYPES } from '@/config/work-items/financeTypes';

export type FinanceWorkflowMode = 'simplified' | 'standard' | 'strict';
export type ProcurementLifecycleMode = 'simplified' | 'standard' | 'strict';

/**
 * Sync direction mode for external accounting system integration.
 *
 * - `two_way`      — full bi-directional sync (inbound take-on + outbound publish)
 * - `publish_only` — push documents out to the accounting system; never pull data in.
 *                    Suitable for clients whose accounting system is the system of
 *                    record for master data and who only want to push AP/AR docs.
 * - `import_only`  — pull data in from the accounting system (replacement for the
 *                    manual ImportManager); never push documents out. Suitable for
 *                    clients using TotalAccess as a front-end/data-entry tool that
 *                    feeds into a separate accounting system manually.
 */
export type ExternalSyncDirection = 'two_way' | 'publish_only' | 'import_only';

/**
 * Sync mode controls whether the external sync runs automatically on a
 * schedule or only when manually triggered.
 * - 'auto': A dynamic schedule is created in the River scheduler to run
 *   the external sync at the configured frequency.
 * - 'manual': No automatic schedule is created; sync only runs when the
 *   user clicks "Sync Now" or an admin triggers it.
 */
export type ExternalSyncMode = 'auto' | 'manual';

/**
 * Sync frequency preset. Each preset maps to a cron expression used when
 * creating the dynamic schedule. The presets are intentionally limited to
 * reasonable options — clients that need a custom cron can use the
 * scheduler API directly.
 */
export type ExternalSyncFrequency =
  | '15min'
  | '30min'
  | 'hourly'
  | '2hours'
  | '4hours'
  | 'daily'
  | 'weekly';

export const DEFAULT_FINANCE_WORKFLOW_MODE: FinanceWorkflowMode = 'standard';
export const DEFAULT_PROCUREMENT_LIFECYCLE_MODE: ProcurementLifecycleMode = 'standard';
export const DEFAULT_EXTERNAL_SYNC_DIRECTION: ExternalSyncDirection = 'two_way';
export const DEFAULT_EXTERNAL_SYNC_MODE: ExternalSyncMode = 'auto';
export const DEFAULT_EXTERNAL_SYNC_FREQUENCY: ExternalSyncFrequency = 'hourly';

/**
 * Map frequency presets to cron expressions (SAST / Africa/Johannesburg).
 * The cron expressions use the standard 5-field format: min hour day month dow.
 */
export const SYNC_FREQUENCY_CRON: Record<ExternalSyncFrequency, string> = {
  '15min': '*/15 * * * *',
  '30min': '*/30 * * * *',
  hourly: '0 * * * *',
  '2hours': '0 */2 * * *',
  '4hours': '0 */4 * * *',
  daily: '0 2 * * *',
  weekly: '0 2 * * 1',
};

/**
 * Human-readable labels for the frequency presets, suitable for display in
 * the UI dropdown.
 */
export const SYNC_FREQUENCY_LABELS: Record<ExternalSyncFrequency, string> = {
  '15min': 'Every 15 minutes',
  '30min': 'Every 30 minutes',
  hourly: 'Every hour',
  '2hours': 'Every 2 hours',
  '4hours': 'Every 4 hours',
  daily: 'Daily (2:00 AM)',
  weekly: 'Weekly (Mondays 2:00 AM)',
};

// Straight-through processing: auto-publish documents above this confidence threshold
// (0–100).  Set to 100 to effectively disable auto-publish.
export const DEFAULT_AUTO_PUBLISH_THRESHOLD = 100;

// Three-way matching defaults
export const DEFAULT_THREE_WAY_MATCH_TOLERANCE = 0.01; // absolute amount
export const DEFAULT_THREE_WAY_MATCH_TOLERANCE_PCT = 2; // 2% percentage tolerance
export const DEFAULT_THREE_WAY_MATCH_AUTO_HOLD = true;
export const DEFAULT_THREE_WAY_MATCH_PARTIAL_MATCH = true;

// Per diem policy defaults
export const DEFAULT_PER_DIEM_PARTIAL_DAY_ENABLED = true;
export const DEFAULT_PER_DIEM_PARTIAL_DAY_PCT = 50;
export const DEFAULT_PER_DIEM_MAX_DAYS_PER_TRIP = 0; // 0 = no limit
export const DEFAULT_PER_DIEM_MAX_DAYS_PER_YEAR = 0; // 0 = no limit
export const DEFAULT_PER_DIEM_RECEIPT_THRESHOLD = 0; // 0 = never
export const DEFAULT_PER_DIEM_REQUIRE_APPROVAL_ABOVE = 0; // 0 = always
export const DEFAULT_PER_DIEM_APPROVAL_LEVEL = 1; // 1=manager
export const DEFAULT_PER_DIEM_FIRST_DAY_PARTIAL = true;
export const DEFAULT_PER_DIEM_LAST_DAY_PARTIAL = true;

// Corporate income tax defaults
// Used by the P&L report when a client has not set an explicit rate.
// The fallback is inferred from the client's country in client_details.
export const DEFAULT_CORPORATE_TAX_RATE = 0.27; // South Africa (reduced from 28% in 2023)

/**
 * Country-to-corporate-tax-rate map for jurisdictions commonly used by
 * TotalAccess clients. Rates are expressed as fractions (0.27 = 27%).
 * When a client's country is not found here, DEFAULT_CORPORATE_TAX_RATE
 * is used as the final fallback.
 */
export const COUNTRY_CORPORATE_TAX_RATES: Record<string, number> = {
  // Southern Africa
  'south africa': 0.27,
  'botswana': 0.22,
  'namibia': 0.32,
  'zambia': 0.30,
  'eswatini': 0.275,
  'swaziland': 0.275, // legacy name
  'lesotho': 0.25,
  'mozambique': 0.32,
  'zimbabwe': 0.2472,
  'malawi': 0.30,
  'angola': 0.25,
  // Other common jurisdictions
  'united kingdom': 0.25,
  'uk': 0.25,
  'united states': 0.21, // federal; state varies
  'usa': 0.21,
  'mauritius': 0.15,
  'kenya': 0.30,
  'nigeria': 0.30,
  'ghana': 0.25,
  'tanzania': 0.30,
  'rwanda': 0.30,
  'uganda': 0.30,
  'dubai': 0.09,
  'uae': 0.09,
  'united arab emirates': 0.09,
};

/**
 * Resolve the corporate tax rate for a client.
 *
 * Resolution order:
 *  1. Explicit `corporate_tax_rate` column on work_item_settings (if set and valid)
 *  2. Country-based lookup from client_details.physical_address_country
 *  3. DEFAULT_CORPORATE_TAX_RATE (27% — South Africa)
 */
export function resolveCorporateTaxRate(
  explicitRate: unknown,
  country?: string | null,
): number {
  // 1. Explicit per-client override
  if (typeof explicitRate === 'number' && Number.isFinite(explicitRate) && explicitRate > 0 && explicitRate <= 1) {
    return explicitRate;
  }
  // 2. Country-based fallback
  if (country) {
    const normalized = country.trim().toLowerCase();
    const countryRate = COUNTRY_CORPORATE_TAX_RATES[normalized];
    if (typeof countryRate === 'number' && countryRate > 0 && countryRate <= 1) {
      return countryRate;
    }
  }
  // 3. Final fallback
  return DEFAULT_CORPORATE_TAX_RATE;
}

const FINANCE_WORKFLOW_MODE_VALUES = new Set<FinanceWorkflowMode>(['simplified', 'standard', 'strict']);
const PROCUREMENT_LIFECYCLE_MODE_VALUES = new Set<ProcurementLifecycleMode>(['simplified', 'standard', 'strict']);

// Inventory tracking settings
export const DEFAULT_INVENTORY_TRACKING_ENABLED = false;
export const DEFAULT_INVENTORY_AUTO_STOCK_ADJUSTMENT = false;
/**
 * Whether to push per-movement QBO InventoryAdjustment records for
 * issue-to-job and quick-receive. Default false because most clients
 * bill jobs with parts (invoice ItemRef lines decrement QBO) and push
 * supplier bills (which increment QBO). Enabling this for those clients
 * causes double-counting. Only enable for clients whose workflow
 * consumes stock that is never billed (internal use, wastage).
 */
export const DEFAULT_QBO_MOVEMENT_ADJUSTMENTS = false;

function toMode(value: unknown): FinanceWorkflowMode | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  return FINANCE_WORKFLOW_MODE_VALUES.has(normalized as FinanceWorkflowMode)
    ? (normalized as FinanceWorkflowMode)
    : null;
}

function getCustomSettingsRecord(settings: unknown): Record<string, unknown> | null {
  if (typeof settings === 'string') {
    try {
      const parsed = JSON.parse(settings);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }

  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return null;
  }

  return settings as Record<string, unknown>;
}

export function resolveFinanceWorkflowMode(customSettings: unknown): FinanceWorkflowMode {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_FINANCE_WORKFLOW_MODE;
  }

  return toMode(record.workflow_mode)
    || toMode(record.billing_workflow_mode)
    || toMode(record.proposal_workflow_mode)
    || DEFAULT_FINANCE_WORKFLOW_MODE;
}

export function isStrictFinanceWorkflow(customSettings: unknown): boolean {
  return resolveFinanceWorkflowMode(customSettings) === 'strict';
}

export function isSimplifiedFinanceWorkflow(customSettings: unknown): boolean {
  return resolveFinanceWorkflowMode(customSettings) === 'simplified';
}

export function normalizeFinanceWorkflowMode(value: unknown): FinanceWorkflowMode {
  return toMode(value) || DEFAULT_FINANCE_WORKFLOW_MODE;
}

export function isFinanceWorkflowModeOnlyUpdate(value: unknown): value is { custom_settings: { workflow_mode?: unknown; billing_workflow_mode?: unknown; proposal_workflow_mode?: unknown; approval_workflows?: unknown; expense_account_mappings?: unknown; expense_doc_type_mode?: unknown } } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const rootRecord = value as Record<string, unknown>;
  if (rootRecord.custom_settings === undefined || Object.keys(rootRecord).some((key) => key !== 'custom_settings')) {
    return false;
  }

  const customSettings = rootRecord.custom_settings;
  if (!customSettings || typeof customSettings !== 'object' || Array.isArray(customSettings)) {
    return false;
  }

  const allowedKeys = new Set(['workflow_mode', 'billing_workflow_mode', 'proposal_workflow_mode', 'approval_workflows', 'expense_account_mappings', 'expense_doc_type_mode', 'inventory_tracking_enabled', 'inventory_auto_stock_adjustment', 'qbo_movement_adjustments', 'procurement_lifecycle_mode', 'auto_publish_threshold', 'auto_publish_doc_types', 'auto_publish_max_amount', 'auto_external_publish_enabled', 'auto_external_publish_adapter', 'auto_external_publish_doc_types', 'auto_external_publish_max_amount', 'auto_external_sync_enabled', 'auto_external_sync_interval_hours', 'auto_external_sync_stage_transactions', 'auto_external_sync_excluded_data_types', 'external_sync_direction', 'external_sync_authority', 'xero_publish_status', 'three_way_match_tolerance', 'three_way_match_tolerance_pct', 'three_way_match_auto_hold', 'three_way_match_partial_match', 'per_diem_partial_day_enabled', 'per_diem_partial_day_pct', 'per_diem_max_days_per_trip', 'per_diem_max_days_per_year', 'per_diem_receipt_threshold', 'per_diem_require_approval_above', 'per_diem_approval_level', 'per_diem_first_day_partial', 'per_diem_last_day_partial', 'enabled_document_types', 'external_publish_date_source']);
  return Object.keys(customSettings as Record<string, unknown>).every((key) => allowedKeys.has(key))
    && Object.keys(customSettings as Record<string, unknown>).length > 0;
}

// Inventory tracking helper functions
export function resolveInventoryTrackingEnabled(customSettings: unknown): boolean {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_INVENTORY_TRACKING_ENABLED;
  }

  const value = record.inventory_tracking_enabled;
  if (typeof value === 'boolean') {
    return value;
  }

  return DEFAULT_INVENTORY_TRACKING_ENABLED;
}

export function resolveInventoryAutoStockAdjustment(customSettings: unknown): boolean {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_INVENTORY_AUTO_STOCK_ADJUSTMENT;
  }

  const value = record.inventory_auto_stock_adjustment;
  if (typeof value === 'boolean') {
    return value;
  }

  return DEFAULT_INVENTORY_AUTO_STOCK_ADJUSTMENT;
}

/**
 * Resolve whether per-movement QBO InventoryAdjustment records should be
 * pushed for issue-to-job and quick-receive. Default false to avoid
 * double-counting with invoice/bill ItemRef lines.
 */
export function resolveQboMovementAdjustments(customSettings: unknown): boolean {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_QBO_MOVEMENT_ADJUSTMENTS;
  }

  const value = record.qbo_movement_adjustments;
  if (typeof value === 'boolean') {
    return value;
  }

  return DEFAULT_QBO_MOVEMENT_ADJUSTMENTS;
}

export function isInventoryTrackingEnabled(customSettings: unknown): boolean {
  return resolveInventoryTrackingEnabled(customSettings);
}

export function isInventoryAutoStockAdjustment(customSettings: unknown): boolean {
  return resolveInventoryAutoStockAdjustment(customSettings);
}

// ── Enabled document types (client preference, capped by subscription) ───────

/**
 * Resolve the client's enabled document types from custom_settings.
 *
 * Returns a Record<string, boolean> mapping document type → enabled.
 * A doc type is "enabled" if:
 *   1. It is allowed by the subscription tier (checked by the caller via
 *      tierAccess.isFinanceDocumentTypeAllowed)
 *   2. The client has explicitly set it to true in enabled_document_types
 *
 * If enabled_document_types is not set or empty, all subscription-allowed
 * types are considered enabled (backwards-compatible default).
 */
export function resolveEnabledDocumentTypes(
  customSettings: unknown,
): Record<string, boolean> {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return {};
  }

  const value = record.enabled_document_types;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const result: Record<string, boolean> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (typeof val === 'boolean') {
        result[key] = val;
      }
    }
    return result;
  }

  return {};
}

/**
 * Check if a specific document type is enabled by the client's preferences.
 * Returns true if the client has no preferences set (backwards-compatible).
 */
export function isDocumentTypeEnabled(
  customSettings: unknown,
  docType: string,
): boolean {
  const enabled = resolveEnabledDocumentTypes(customSettings);
  if (Object.keys(enabled).length === 0) {
    return true; // No preferences = all enabled
  }
  return enabled[docType] === true;
}

// ── Backend enforcement helper ──────────────────────────────────────────────

/**
 * Error thrown by {@link assertDocumentTypeEnabled} when a client has
 * explicitly disabled a finance document type in HubSettings.
 */
export class DocumentTypeDisabledError extends Error {
  readonly docType: string;
  constructor(docType: string) {
    super(
      `Document type '${docType}' is disabled for this workspace. Enable it in Settings → Document Types to use it.`,
    );
    this.name = 'DocumentTypeDisabledError';
    this.docType = docType;
  }
}

/**
 * Backend guard: fetches the client's `enabled_document_types` preference
 * from `work_item_settings.custom_settings` and throws
 * {@link DocumentTypeDisabledError} (HTTP 403) when the given `docType` is a
 * finance document type that the client has explicitly disabled.
 *
 * No-op when:
 *  - `docType` is not in {@link FINANCE_DOCUMENT_TYPES} (non-finance types
 *    like `job`, `appointment`, `task` are never gated by this setting).
 *  - The client has no `enabled_document_types` preference set (empty =
 *    all enabled, backwards-compatible).
 *
 * Usage in API routes:
 * ```ts
 * try {
 *   await assertDocumentTypeEnabled(db, client_id, validatedData.item_type);
 * } catch (err) {
 *   if (err instanceof DocumentTypeDisabledError) {
 *     return NextResponse.json({ success: false, error: err.message }, { status: 403 });
 *   }
 *   throw err;
 * }
 * ```
 */
export async function assertDocumentTypeEnabled(
  knex: import('knex').Knex,
  clientId: number,
  docType: string,
): Promise<void> {
  // Only finance document types are gated by enabled_document_types.
  // Non-finance work item types (job, appointment, task, etc.) are always allowed.
  if (!(FINANCE_DOCUMENT_TYPES as readonly string[]).includes(docType)) {
    return;
  }

  const settingsRow = await knex('work_item_settings')
    .where({ client_id: clientId })
    .select('custom_settings')
    .first();

  const enabledDocTypes = resolveEnabledDocumentTypes(settingsRow?.custom_settings);
  if (Object.keys(enabledDocTypes).length > 0 && enabledDocTypes[docType] !== true) {
    throw new DocumentTypeDisabledError(docType);
  }
}

// ── Auto-publish threshold ───────────────────────────────────────────────────

export function resolveAutoPublishThreshold(customSettings: unknown): number {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_AUTO_PUBLISH_THRESHOLD;
  }

  const value = record.auto_publish_threshold;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100) {
    return value;
  }

  return DEFAULT_AUTO_PUBLISH_THRESHOLD;
}

// ── External auto-publish (ready → external accounting system) ────────────────

export const DEFAULT_AUTO_EXTERNAL_PUBLISH_ENABLED = false;
export const DEFAULT_AUTO_EXTERNAL_PUBLISH_ADAPTER = '';
export const DEFAULT_AUTO_EXTERNAL_PUBLISH_MAX_AMOUNT = 0;

/**
 * Xero invoice status on publish. Xero accepts `DRAFT` (default, safe — requires
 * manual approval in Xero) or `AUTHORISED` (straight-through — appears in reports
 * and bank reconciliation immediately). Some clients want straight-through posting
 * so they don't have to approve twice (once in TotalAccess, once in Xero).
 */
export type XeroPublishStatus = 'DRAFT' | 'AUTHORISED';
export const DEFAULT_XERO_PUBLISH_STATUS: XeroPublishStatus = 'DRAFT';

export interface ExternalAutoPublishConfig {
  enabled: boolean;
  adapterId: string;
  docTypes: Record<string, boolean>;
  maxAmount: number;
}

export function resolveExternalAutoPublishConfig(customSettings: unknown): ExternalAutoPublishConfig {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return {
      enabled: DEFAULT_AUTO_EXTERNAL_PUBLISH_ENABLED,
      adapterId: DEFAULT_AUTO_EXTERNAL_PUBLISH_ADAPTER,
      docTypes: {},
      maxAmount: DEFAULT_AUTO_EXTERNAL_PUBLISH_MAX_AMOUNT,
    };
  }

  const enabled = typeof record.auto_external_publish_enabled === 'boolean'
    ? record.auto_external_publish_enabled : DEFAULT_AUTO_EXTERNAL_PUBLISH_ENABLED;

  const adapterId = typeof record.auto_external_publish_adapter === 'string'
    ? record.auto_external_publish_adapter : DEFAULT_AUTO_EXTERNAL_PUBLISH_ADAPTER;

  const docTypes = (record.auto_external_publish_doc_types && typeof record.auto_external_publish_doc_types === 'object' && !Array.isArray(record.auto_external_publish_doc_types))
    ? record.auto_external_publish_doc_types as Record<string, boolean> : {};

  const maxAmount = typeof record.auto_external_publish_max_amount === 'number'
    && Number.isFinite(record.auto_external_publish_max_amount) && record.auto_external_publish_max_amount >= 0
    ? record.auto_external_publish_max_amount : DEFAULT_AUTO_EXTERNAL_PUBLISH_MAX_AMOUNT;

  // queued_publish: if any active approval chain has publish_trigger='queued_publish',
  // treat auto-publish as enabled so the batch cron processes these documents.
  // The adapter is resolved from the chain's default_adapter or the external_sync_authority.
  const hasQueuedPublishChain = Array.isArray(record.approval_chains)
    && (record.approval_chains as Array<Record<string, unknown>>).some(
      (chain) => chain.is_active === true && chain.publish_trigger === 'queued_publish',
    );

  if (hasQueuedPublishChain) {
    // Resolve adapter from the chain, external_sync_authority.default_adapter,
    // or fall back to auto_external_publish_adapter
    const chains = record.approval_chains as Array<Record<string, unknown>>;
    const queuedChain = chains.find((c) => c.is_active === true && c.publish_trigger === 'queued_publish');
    const authority = (record.external_sync_authority && typeof record.external_sync_authority === 'object')
      ? record.external_sync_authority as Record<string, unknown>
      : {};
    const chainAdapter = (queuedChain?.default_adapter as string)
      || (authority.default_adapter as string)
      || adapterId;

    return {
      enabled: true,
      adapterId: chainAdapter || adapterId,
      docTypes,
      maxAmount,
    };
  }

  return { enabled, adapterId, docTypes, maxAmount };
}

// ── Expense document type mode ────────────────────────────────────────────────
//
// How expense-like intake documents (DIN → promoted document) are classified:
//  - 'auto' (default): COD suppliers (payment_terms_days = 0) → expense (EXP),
//    marked paid at capture; terms suppliers → supplier_invoice (BIL), unpaid.
//  - 'always_supplier_invoice': every expense is created as a BIL. COD
//    suppliers are still marked paid at capture; terms suppliers stay unpaid.

export type ExpenseDocTypeMode = 'auto' | 'always_supplier_invoice';
export const DEFAULT_EXPENSE_DOC_TYPE_MODE: ExpenseDocTypeMode = 'auto';

export function resolveExpenseDocTypeMode(customSettings: unknown): ExpenseDocTypeMode {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_EXPENSE_DOC_TYPE_MODE;
  }
  return record.expense_doc_type_mode === 'always_supplier_invoice'
    ? 'always_supplier_invoice'
    : DEFAULT_EXPENSE_DOC_TYPE_MODE;
}

// ── External publish date source ─────────────────────────────────────────────
// Controls which date lands on the external document (QBO TxnDate, Xero Date,
// etc.) when a work item is published to an accounting adapter.
//  - 'intake_date' (default, backwards-compatible): the document intake/issue
//    date — work_items.issued_at wins over the receipt/transaction date.
//  - 'transaction_date': the actual transaction date on the supplier slip —
//    work_items.expense_date / edited_payload.transaction_date /
//    extraction_payload.transaction_date win over issued_at. Some clients
//    (e.g. those that batch-capture slips weeks after the fact) need the
//    external bill dated to the slip, not the intake day.
export type PublishDateSource = 'intake_date' | 'transaction_date';
export const DEFAULT_PUBLISH_DATE_SOURCE: PublishDateSource = 'intake_date';

export function resolvePublishDateSource(customSettings: unknown): PublishDateSource {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_PUBLISH_DATE_SOURCE;
  }
  return record.external_publish_date_source === 'transaction_date'
    ? 'transaction_date'
    : DEFAULT_PUBLISH_DATE_SOURCE;
}

// ── Phase 3: Collections & disputes auto-sync toggles ─────────────────────────
//
// These toggles gate the auto-sync paths in the collections and disputes routes:
//  - auto_sync_promise_payments: when a promise to pay is marked kept, auto-publish
//    a QBO ReceivePayment (collections route).
//  - auto_sync_write_offs: when an invoice is written off (collections or dispute
//    resolution), auto-publish a bad-debt CreditMemo (collections + disputes routes).
// Both default to false so existing clients see no behaviour change until opted in.

export const DEFAULT_AUTO_SYNC_PROMISE_PAYMENTS = false;
export const DEFAULT_AUTO_SYNC_WRITE_OFFS = false;

export interface CollectionsSyncConfig {
  autoSyncPromisePayments: boolean;
  autoSyncWriteOffs: boolean;
}

export function resolveCollectionsSyncConfig(customSettings: unknown): CollectionsSyncConfig {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return {
      autoSyncPromisePayments: DEFAULT_AUTO_SYNC_PROMISE_PAYMENTS,
      autoSyncWriteOffs: DEFAULT_AUTO_SYNC_WRITE_OFFS,
    };
  }
  return {
    autoSyncPromisePayments: typeof record.auto_sync_promise_payments === 'boolean'
      ? record.auto_sync_promise_payments : DEFAULT_AUTO_SYNC_PROMISE_PAYMENTS,
    autoSyncWriteOffs: typeof record.auto_sync_write_offs === 'boolean'
      ? record.auto_sync_write_offs : DEFAULT_AUTO_SYNC_WRITE_OFFS,
  };
}

// ── External sync direction mode (two_way | publish_only | import_only) ───────

const SYNC_DIRECTION_VALUES = new Set<ExternalSyncDirection>(['two_way', 'publish_only', 'import_only']);

export function resolveExternalSyncDirection(customSettings: unknown): ExternalSyncDirection {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_EXTERNAL_SYNC_DIRECTION;
  }
  const value = record.external_sync_direction;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (SYNC_DIRECTION_VALUES.has(normalized as ExternalSyncDirection)) {
      return normalized as ExternalSyncDirection;
    }
  }
  return DEFAULT_EXTERNAL_SYNC_DIRECTION;
}

export function isPublishOnlySync(customSettings: unknown): boolean {
  return resolveExternalSyncDirection(customSettings) === 'publish_only';
}

export function isImportOnlySync(customSettings: unknown): boolean {
  return resolveExternalSyncDirection(customSettings) === 'import_only';
}

export function isTwoWaySync(customSettings: unknown): boolean {
  return resolveExternalSyncDirection(customSettings) === 'two_way';
}

// ── External sync mode + frequency (auto vs manual scheduling) ──────────────

const SYNC_MODE_VALUES = new Set<ExternalSyncMode>(['auto', 'manual']);
const SYNC_FREQUENCY_VALUES = new Set<ExternalSyncFrequency>([
  '15min', '30min', 'hourly', '2hours', '4hours', 'daily', 'weekly',
]);

export function resolveExternalSyncMode(customSettings: unknown): ExternalSyncMode {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) return DEFAULT_EXTERNAL_SYNC_MODE;
  const value = record.external_sync_mode;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (SYNC_MODE_VALUES.has(normalized as ExternalSyncMode)) {
      return normalized as ExternalSyncMode;
    }
  }
  return DEFAULT_EXTERNAL_SYNC_MODE;
}

export function resolveExternalSyncFrequency(customSettings: unknown): ExternalSyncFrequency {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) return DEFAULT_EXTERNAL_SYNC_FREQUENCY;
  const value = record.external_sync_frequency;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (SYNC_FREQUENCY_VALUES.has(normalized as ExternalSyncFrequency)) {
      return normalized as ExternalSyncFrequency;
    }
  }
  return DEFAULT_EXTERNAL_SYNC_FREQUENCY;
}

// ── Phase 2: Authority model (master/slave/hybrid per data type) ──────────────
//
// The authority model declares which system is the source of truth for each
// data type. It refines the coarse-grained `external_sync_direction` setting:
//   - client_master  → publish_only (we push out, pull reference data only)
//   - external_master → import_only  (we pull in, never push master data)
//   - hybrid          → two_way       (per-data-type authority drives push/pull)
//
// Operational data types (collection_actions, disputes, landed_costs,
// expense_extractions, approval_state, sales_orders, work_orders) are
// always 'client' — they have no external-system equivalent.

export type ExternalSyncAuthorityMode = 'client_master' | 'external_master' | 'hybrid';
export type ConflictResolutionStrategy = 'client_wins' | 'external_wins' | 'newest_wins' | 'manual' | 'external_wins_copy';

/** Per-data-type authority: 'client' = our system wins, 'external' = QBO wins. */
export type DataTypeAuthority =
  | 'chart_of_accounts' | 'contacts' | 'products' | 'invoices' | 'bills'
  | 'payments' | 'credit_notes' | 'bank_transactions' | 'inventory_items'
  | 'inventory_adjustments' | 'journal_entries' | 'collection_actions'
  | 'disputes' | 'write_offs' | 'landed_costs' | 'quotes' | 'sales_orders'
  | 'work_orders'
  | 'purchase_orders' | 'supplier_credits' | 'document_attachments'
  | 'expense_documents' | 'expense_extractions' | 'approval_state' | 'budgets';

/**
 * Data types that are always client-authoritative.
 *
 * Some data types have no external equivalent (collection_actions, disputes,
 * approval_state, etc.). Others have an external equivalent but Total Finance
 * is always the master because it provides enterprise-grade functionality
 * that the external system (e.g. QBO) cannot match:
 *   - `inventory_items` — Total Finance has an enterprise inventory system
 *     (multi-warehouse, landed costs, serial/batch tracking, FIFO/LIFO/weighted
 *     average valuation). QBO's inventory is basic. Clients always create
 *     inventory in Total Finance and sync to QBO to keep both systems aligned.
 *   - `inventory_adjustments` — Stock adjustments originate in Total Finance
 *     (stocktake variances, manual adjustments, goods returns) and are pushed
 *     to QBO as InventoryAdjustment entities.
 *   - `landed_costs` — No QBO equivalent; Total Finance is always master.
 */
export const ALWAYS_CLIENT_DATA_TYPES: ReadonlySet<DataTypeAuthority> = new Set([
  'collection_actions', 'disputes', 'landed_costs',
  'expense_extractions', 'approval_state', 'sales_orders',
  'inventory_items', 'inventory_adjustments',
  // work_orders have no external-accounting equivalent (QBO has no work
  // order entity) — they are managed entirely inside TotalAccess.
  'work_orders',
]);

/**
 * Per-data-type authority value.
 *
 * - `'client'` — TF is the sole master. TF creates → pushes to external.
 *   Inbound sync imports are blocked (TF already has the record).
 * - `'external'` — QBO is the sole master. QBO creates → pulls to TF.
 *   Outbound publish is blocked (QBO already has the record).
 * - `'co_master'` — Both systems can create and update. TF-created records
 *   get published to QBO (storing external_id). QBO-created records get
 *   imported to TF. Conflict resolution strategy handles edge cases.
 *   This is the recommended mode for clients transitioning fully to TF
 *   while keeping QBO as the accounting record.
 */
export type DataTypeAuthorityValue = 'client' | 'external' | 'co_master';

export interface ExternalSyncAuthority {
  mode: ExternalSyncAuthorityMode;
  data_type_authority: Partial<Record<DataTypeAuthority, DataTypeAuthorityValue>>;
  conflict_resolution: ConflictResolutionStrategy;
  conflict_window_seconds: number;
}

export const DEFAULT_AUTHORITY_MODE: ExternalSyncAuthorityMode = 'external_master';
export const DEFAULT_CONFLICT_RESOLUTION: ConflictResolutionStrategy = 'newest_wins';
export const DEFAULT_CONFLICT_WINDOW_SECONDS = 300; // 5 minutes

const AUTHORITY_MODE_VALUES = new Set<ExternalSyncAuthorityMode>(['client_master', 'external_master', 'hybrid']);
const CONFLICT_RESOLUTION_VALUES = new Set<ConflictResolutionStrategy>(['client_wins', 'external_wins', 'newest_wins', 'manual', 'external_wins_copy']);

/** Default per-data-type authority for external_master mode (client 23). */
export const EXTERNAL_MASTER_DEFAULTS: Record<DataTypeAuthority, DataTypeAuthorityValue> = {
  chart_of_accounts: 'external',
  contacts: 'external',
  products: 'external',
  invoices: 'external',
  bills: 'external',
  payments: 'external',
  credit_notes: 'external',
  bank_transactions: 'external',
  inventory_items: 'external',
  inventory_adjustments: 'external',
  journal_entries: 'external',
  collection_actions: 'client',
  disputes: 'client',
  write_offs: 'client',
  landed_costs: 'client',
  quotes: 'client',
  sales_orders: 'client',
  work_orders: 'client',
  purchase_orders: 'client',
  supplier_credits: 'external',
  document_attachments: 'external',
  expense_documents: 'external',
  expense_extractions: 'client',
  approval_state: 'client',
  budgets: 'client',
};

/** Default per-data-type authority for client_master mode. */
export const CLIENT_MASTER_DEFAULTS: Record<DataTypeAuthority, DataTypeAuthorityValue> = {
  ...EXTERNAL_MASTER_DEFAULTS,
  chart_of_accounts: 'external', // still pull CoA as reference
  contacts: 'external',          // still pull contacts as reference
  products: 'client',
  invoices: 'client',
  bills: 'client',
  payments: 'client',
  credit_notes: 'client',
  bank_transactions: 'external', // bank transactions always from external
  inventory_items: 'client',
  inventory_adjustments: 'client',
  journal_entries: 'client',
  supplier_credits: 'client',
  document_attachments: 'client',
  expense_documents: 'client',
  budgets: 'client',
};

/**
 * Default per-data-type authority for hybrid mode.
 *
 * Hybrid mode is the recommended production mode. It uses three authority
 * levels per data type:
 *
 *   co_master (both systems create → linked by external_id):
 *     - invoices           — FinanceDocumentView: TF creates from billing
 *                            submissions, QBO has existing invoices. Both
 *                            directions sync. Conflict resolution handles
 *                            edge cases.
 *     - bills              — FinanceDocumentView: QBO has supplier bills,
 *                            TF creates from expense intake. Both directions.
 *     - quotes             — TF creates quotes, QBO has estimates. Both.
 *     - credit_notes       — both systems can create credit notes.
 *     - payments           — QBO has payments, TF might create payments.
 *     - purchase_orders    — TF creates POs, QBO might have POs. Both.
 *     - supplier_credits   — QBO VendorCredit, TF might create. Both.
 *     - expense_documents  — ExpenseInbox: TF creates from intake, QBO has
 *                            purchases/bills. Both directions.
 *
 *   TF master (we create → push to QBO):
 *     - inventory_items    — SharedInventoryWorkspace: enterprise inventory
 *     - inventory_adjustments — stock adjustments originate in TF
 *     - budgets            — budget management in TF
 *
 *   External master (QBO creates → we import):
 *     - chart_of_accounts  — reference data from QBO
 *     - contacts           — reference data from QBO
 *     - products           — product catalog from QBO
 *     - bank_transactions  — bank transactions always from external
 *     - journal_entries    — JournalManagement: journals from QBO
 *     - document_attachments — attachments from QBO
 *
 *   Always client (no external equivalent):
 *     - collection_actions, disputes, write_offs, landed_costs,
 *       expense_extractions, approval_state, sales_orders, work_orders
 */
export const HYBRID_DEFAULTS: Record<DataTypeAuthority, DataTypeAuthorityValue> = {
  // Reference data — external master
  chart_of_accounts: 'external',
  contacts: 'external',
  products: 'external',

  // Transactional documents — co-master (both TF and QBO can create)
  // This supports the finance > ops > finance flow where TF creates invoices
  // from billing submissions, while QBO also has invoices that sync to TF.
  invoices: 'co_master',
  bills: 'co_master',
  payments: 'co_master',
  credit_notes: 'co_master',
  supplier_credits: 'co_master',
  expense_documents: 'co_master',

  // Bank transactions — always from external (bank feeds)
  bank_transactions: 'external',

  // Attachments — external master
  document_attachments: 'external',

  // Journal entries — external master (JournalManagement)
  journal_entries: 'external',

  // Inventory — TF master (SharedInventoryWorkspace)
  inventory_items: 'client',
  inventory_adjustments: 'client',

  // Sales & procurement — co-master (TF creates, QBO might have)
  quotes: 'co_master',
  sales_orders: 'client',
  purchase_orders: 'co_master',

  // Budgets — TF master (BudgetManagement)
  budgets: 'client',

  // Operational — always client
  collection_actions: 'client',
  disputes: 'client',
  write_offs: 'client',
  landed_costs: 'client',
  expense_extractions: 'client',
  approval_state: 'client',
  // Work orders — internal ops/fulfillment documents; no QBO equivalent
  work_orders: 'client',
};

/** Map authority mode to sync direction. */
export function authorityModeToDirection(mode: ExternalSyncAuthorityMode): ExternalSyncDirection {
  switch (mode) {
    case 'client_master': return 'publish_only';
    case 'external_master': return 'import_only';
    case 'hybrid': return 'two_way';
  }
}

/** Resolve the authority config from custom_settings, with defaults. */
export function resolveExternalSyncAuthority(customSettings: unknown): ExternalSyncAuthority {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return {
      mode: DEFAULT_AUTHORITY_MODE,
      data_type_authority: {},
      conflict_resolution: DEFAULT_CONFLICT_RESOLUTION,
      conflict_window_seconds: DEFAULT_CONFLICT_WINDOW_SECONDS,
    };
  }

  const raw = record.external_sync_authority;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      mode: DEFAULT_AUTHORITY_MODE,
      data_type_authority: {},
      conflict_resolution: DEFAULT_CONFLICT_RESOLUTION,
      conflict_window_seconds: DEFAULT_CONFLICT_WINDOW_SECONDS,
    };
  }

  const authRecord = raw as Record<string, unknown>;

  const mode = typeof authRecord.mode === 'string' && AUTHORITY_MODE_VALUES.has(authRecord.mode as ExternalSyncAuthorityMode)
    ? authRecord.mode as ExternalSyncAuthorityMode : DEFAULT_AUTHORITY_MODE;

  const conflictResolution = typeof authRecord.conflict_resolution === 'string' && CONFLICT_RESOLUTION_VALUES.has(authRecord.conflict_resolution as ConflictResolutionStrategy)
    ? authRecord.conflict_resolution as ConflictResolutionStrategy : DEFAULT_CONFLICT_RESOLUTION;

  const conflictWindowSeconds = typeof authRecord.conflict_window_seconds === 'number'
    && Number.isFinite(authRecord.conflict_window_seconds) && authRecord.conflict_window_seconds > 0
    ? authRecord.conflict_window_seconds : DEFAULT_CONFLICT_WINDOW_SECONDS;

  const dataTypeAuthority = (authRecord.data_type_authority && typeof authRecord.data_type_authority === 'object' && !Array.isArray(authRecord.data_type_authority))
    ? authRecord.data_type_authority as Partial<Record<DataTypeAuthority, DataTypeAuthorityValue>>
    : {};

  return { mode, data_type_authority: dataTypeAuthority, conflict_resolution: conflictResolution, conflict_window_seconds: conflictWindowSeconds };
}

/** Get the effective authority for a specific data type, applying defaults. */
export function getDataTypeAuthority(
  customSettings: unknown,
  dataType: DataTypeAuthority,
): DataTypeAuthorityValue {
  if (ALWAYS_CLIENT_DATA_TYPES.has(dataType)) return 'client';
  const auth = resolveExternalSyncAuthority(customSettings);
  // Explicit per-data-type override wins
  const explicit = auth.data_type_authority[dataType];
  if (explicit === 'client' || explicit === 'external' || explicit === 'co_master') return explicit;
  // Fall back to mode-based defaults
  const defaults = auth.mode === 'client_master'
    ? CLIENT_MASTER_DEFAULTS
    : auth.mode === 'hybrid'
      ? HYBRID_DEFAULTS
      : EXTERNAL_MASTER_DEFAULTS;
  return defaults[dataType] ?? 'external';
}

/** Check if outbound publish is allowed for a data type given the authority config. */
export function isPublishAllowed(customSettings: unknown, dataType: DataTypeAuthority): boolean {
  const authority = getDataTypeAuthority(customSettings, dataType);
  // Both 'client' and 'co_master' allow outbound publish.
  // 'co_master' means TF can create records and push them to QBO.
  return authority === 'client' || authority === 'co_master';
}

/** Check if inbound sync is allowed for a data type given the authority config. */
export function isImportAllowed(customSettings: unknown, dataType: DataTypeAuthority): boolean {
  const authority = getDataTypeAuthority(customSettings, dataType);
  // Both 'external' and 'co_master' allow inbound sync.
  // 'co_master' means QBO-created records get imported to TF.
  return authority === 'external' || authority === 'co_master';
}

// ── Xero publish status (DRAFT vs AUTHORISED) ─────────────────────────────────

const XERO_PUBLISH_STATUS_VALUES = new Set<XeroPublishStatus>(['DRAFT', 'AUTHORISED']);

export function resolveXeroPublishStatus(customSettings: unknown): XeroPublishStatus {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_XERO_PUBLISH_STATUS;
  }
  const value = record.xero_publish_status;
  if (typeof value === 'string') {
    const normalized = value.trim().toUpperCase();
    if (XERO_PUBLISH_STATUS_VALUES.has(normalized as XeroPublishStatus)) {
      return normalized as XeroPublishStatus;
    }
  }
  return DEFAULT_XERO_PUBLISH_STATUS;
}

// ── External inbound sync (external accounting system → our DB) ───────────────

/**
 * Inbound sync defaults to ENABLED for any connected adapter. Connecting an
 * accounting system is an explicit act, and a connection that silently never
 * syncs is the failure mode this config exists to prevent — so the opt-out is
 * the setting, not the opt-in.
 */
export const DEFAULT_AUTO_EXTERNAL_SYNC_ENABLED = true;
export const DEFAULT_AUTO_EXTERNAL_SYNC_INTERVAL_HOURS = 6;
export const DEFAULT_AUTO_EXTERNAL_SYNC_STAGE_TRANSACTIONS = true;

export interface ExternalAutoSyncConfig {
  enabled: boolean;
  /** Minimum hours between autonomous syncs for a client. */
  intervalHours: number;
  /** When true, bank transactions are fetched but held for review, not posted. */
  stageTransactions: boolean;
  /** Data types to exclude from the autonomous run. */
  excludedDataTypes: string[];
}

export function resolveExternalAutoSyncConfig(customSettings: unknown): ExternalAutoSyncConfig {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return {
      enabled: DEFAULT_AUTO_EXTERNAL_SYNC_ENABLED,
      intervalHours: DEFAULT_AUTO_EXTERNAL_SYNC_INTERVAL_HOURS,
      stageTransactions: DEFAULT_AUTO_EXTERNAL_SYNC_STAGE_TRANSACTIONS,
      excludedDataTypes: [],
    };
  }

  const enabled = typeof record.auto_external_sync_enabled === 'boolean'
    ? record.auto_external_sync_enabled : DEFAULT_AUTO_EXTERNAL_SYNC_ENABLED;

  const intervalHours = typeof record.auto_external_sync_interval_hours === 'number'
    && Number.isFinite(record.auto_external_sync_interval_hours)
    && record.auto_external_sync_interval_hours > 0
    ? record.auto_external_sync_interval_hours : DEFAULT_AUTO_EXTERNAL_SYNC_INTERVAL_HOURS;

  const stageTransactions = typeof record.auto_external_sync_stage_transactions === 'boolean'
    ? record.auto_external_sync_stage_transactions : DEFAULT_AUTO_EXTERNAL_SYNC_STAGE_TRANSACTIONS;

  const excludedDataTypes = Array.isArray(record.auto_external_sync_excluded_data_types)
    ? (record.auto_external_sync_excluded_data_types as unknown[]).filter((v): v is string => typeof v === 'string')
    : [];

  return { enabled, intervalHours, stageTransactions, excludedDataTypes };
}

// ── Procurement lifecycle mode ──────────────────────────────────────────────

function toProcurementMode(value: unknown): ProcurementLifecycleMode | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  return PROCUREMENT_LIFECYCLE_MODE_VALUES.has(normalized as ProcurementLifecycleMode)
    ? (normalized as ProcurementLifecycleMode)
    : null;
}

/**
 * Procurement lifecycle mode is folded into the organisation-wide workflow
 * mode (`workflow_mode`). The legacy `procurement_lifecycle_mode` key is only
 * honoured for orgs that have never recorded a workflow_mode; once the org
 * mode exists it wins, and the settings PATCH mirrors it back into
 * `procurement_lifecycle_mode` so older readers stay consistent.
 */
export function resolveProcurementLifecycleMode(customSettings: unknown): ProcurementLifecycleMode {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return DEFAULT_PROCUREMENT_LIFECYCLE_MODE;
  }

  const orgMode = toMode(record.workflow_mode)
    || toMode(record.billing_workflow_mode)
    || toMode(record.proposal_workflow_mode);

  if (orgMode) {
    return orgMode as ProcurementLifecycleMode;
  }

  return toProcurementMode(record.procurement_lifecycle_mode)
    || DEFAULT_PROCUREMENT_LIFECYCLE_MODE;
}

export function normalizeProcurementLifecycleMode(value: unknown): ProcurementLifecycleMode {
  return toProcurementMode(value) || DEFAULT_PROCUREMENT_LIFECYCLE_MODE;
}

export function isStrictProcurementLifecycle(customSettings: unknown): boolean {
  return resolveProcurementLifecycleMode(customSettings) === 'strict';
}

export function isSimplifiedProcurementLifecycle(customSettings: unknown): boolean {
  return resolveProcurementLifecycleMode(customSettings) === 'simplified';
}

// ── Three-way matching tolerance config ──────────────────────────────────────

export interface ThreeWayMatchConfig {
  tolerance: number;
  tolerancePct: number;
  autoHold: boolean;
  partialMatch: boolean;
}

export function resolveThreeWayMatchConfig(customSettings: unknown): ThreeWayMatchConfig {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return {
      tolerance: DEFAULT_THREE_WAY_MATCH_TOLERANCE,
      tolerancePct: DEFAULT_THREE_WAY_MATCH_TOLERANCE_PCT,
      autoHold: DEFAULT_THREE_WAY_MATCH_AUTO_HOLD,
      partialMatch: DEFAULT_THREE_WAY_MATCH_PARTIAL_MATCH,
    };
  }

  const tolerance = typeof record.three_way_match_tolerance === 'number'
    && Number.isFinite(record.three_way_match_tolerance) && record.three_way_match_tolerance >= 0
    ? record.three_way_match_tolerance : DEFAULT_THREE_WAY_MATCH_TOLERANCE;

  const tolerancePct = typeof record.three_way_match_tolerance_pct === 'number'
    && Number.isFinite(record.three_way_match_tolerance_pct) && record.three_way_match_tolerance_pct >= 0 && record.three_way_match_tolerance_pct <= 100
    ? record.three_way_match_tolerance_pct : DEFAULT_THREE_WAY_MATCH_TOLERANCE_PCT;

  const autoHold = typeof record.three_way_match_auto_hold === 'boolean'
    ? record.three_way_match_auto_hold : DEFAULT_THREE_WAY_MATCH_AUTO_HOLD;

  const partialMatch = typeof record.three_way_match_partial_match === 'boolean'
    ? record.three_way_match_partial_match : DEFAULT_THREE_WAY_MATCH_PARTIAL_MATCH;

  return { tolerance, tolerancePct, autoHold, partialMatch };
}

// ── Per diem policy config ───────────────────────────────────────────────────

export interface PerDiemPolicySettings {
  partialDayEnabled: boolean;
  partialDayPercentage: number;
  maxDaysPerTrip: number;
  maxDaysPerYear: number;
  receiptThreshold: number;
  requireApprovalAbove: number;
  approvalLevel: number;
  firstDayPartial: boolean;
  lastDayPartial: boolean;
}

export function resolvePerDiemPolicySettings(customSettings: unknown): PerDiemPolicySettings {
  const record = getCustomSettingsRecord(customSettings);
  if (!record) {
    return {
      partialDayEnabled: DEFAULT_PER_DIEM_PARTIAL_DAY_ENABLED,
      partialDayPercentage: DEFAULT_PER_DIEM_PARTIAL_DAY_PCT,
      maxDaysPerTrip: DEFAULT_PER_DIEM_MAX_DAYS_PER_TRIP,
      maxDaysPerYear: DEFAULT_PER_DIEM_MAX_DAYS_PER_YEAR,
      receiptThreshold: DEFAULT_PER_DIEM_RECEIPT_THRESHOLD,
      requireApprovalAbove: DEFAULT_PER_DIEM_REQUIRE_APPROVAL_ABOVE,
      approvalLevel: DEFAULT_PER_DIEM_APPROVAL_LEVEL,
      firstDayPartial: DEFAULT_PER_DIEM_FIRST_DAY_PARTIAL,
      lastDayPartial: DEFAULT_PER_DIEM_LAST_DAY_PARTIAL,
    };
  }

  const partialDayEnabled = typeof record.per_diem_partial_day_enabled === 'boolean'
    ? record.per_diem_partial_day_enabled : DEFAULT_PER_DIEM_PARTIAL_DAY_ENABLED;

  const partialDayPercentage = typeof record.per_diem_partial_day_pct === 'number'
    && record.per_diem_partial_day_pct >= 0 && record.per_diem_partial_day_pct <= 100
    ? record.per_diem_partial_day_pct : DEFAULT_PER_DIEM_PARTIAL_DAY_PCT;

  const maxDaysPerTrip = typeof record.per_diem_max_days_per_trip === 'number'
    && record.per_diem_max_days_per_trip >= 0
    ? record.per_diem_max_days_per_trip : DEFAULT_PER_DIEM_MAX_DAYS_PER_TRIP;

  const maxDaysPerYear = typeof record.per_diem_max_days_per_year === 'number'
    && record.per_diem_max_days_per_year >= 0
    ? record.per_diem_max_days_per_year : DEFAULT_PER_DIEM_MAX_DAYS_PER_YEAR;

  const receiptThreshold = typeof record.per_diem_receipt_threshold === 'number'
    && record.per_diem_receipt_threshold >= 0
    ? record.per_diem_receipt_threshold : DEFAULT_PER_DIEM_RECEIPT_THRESHOLD;

  const requireApprovalAbove = typeof record.per_diem_require_approval_above === 'number'
    && record.per_diem_require_approval_above >= 0
    ? record.per_diem_require_approval_above : DEFAULT_PER_DIEM_REQUIRE_APPROVAL_ABOVE;

  const approvalLevel = typeof record.per_diem_approval_level === 'number'
    && record.per_diem_approval_level >= 1 && record.per_diem_approval_level <= 3
    ? record.per_diem_approval_level : DEFAULT_PER_DIEM_APPROVAL_LEVEL;

  const firstDayPartial = typeof record.per_diem_first_day_partial === 'boolean'
    ? record.per_diem_first_day_partial : DEFAULT_PER_DIEM_FIRST_DAY_PARTIAL;

  const lastDayPartial = typeof record.per_diem_last_day_partial === 'boolean'
    ? record.per_diem_last_day_partial : DEFAULT_PER_DIEM_LAST_DAY_PARTIAL;

  return { partialDayEnabled, partialDayPercentage, maxDaysPerTrip, maxDaysPerYear, receiptThreshold, requireApprovalAbove, approvalLevel, firstDayPartial, lastDayPartial };
}
